import { useField, useFormikContext } from 'formik';
import { type FC, useEffect, useState } from 'react';

import type { EntityType } from '@core/types/search';
import { hasStringValue, hasValue } from '@core/utils/equality';
import { SetFormikValues } from '@core/utils/formikHelpers';

import { EntitySearchInputBase, type EntitySelection } from './EntitySearchInputBase';

export type { EntitySelection };

export type EntitySearchSiblingValues = Record<string, unknown>;

type Props = {
  name: string;
  entityTypes: EntityType[];
  label?: string;
  initialSelection?: EntitySelection | null;
  onSelect?: (selection: EntitySelection) => EntitySearchSiblingValues | void;
  onClear?: () => EntitySearchSiblingValues | void;
  placeholder?: string;
  disabled?: boolean;
  tip?: string;
  limitPerEntity?: number;
};

export const EntitySearchInput: FC<Props> = ({
  name,
  entityTypes,
  label,
  initialSelection = null,
  onSelect,
  onClear,
  placeholder,
  disabled,
  tip,
  limitPerEntity,
}) => {
  const formikBag = useFormikContext<Record<string, unknown>>();
  const [field, meta] = useField<string>(name);
  const [selection, setSelection] = useState<EntitySelection | null>(initialSelection);

  useEffect(() => {
    if (!hasStringValue(field.value)) {
      setSelection(null);
    } else if (field.value === initialSelection?.id) {
      setSelection(initialSelection);
    }
  }, [field.value, initialSelection]);

  const handleChange = (next: EntitySelection | null): void => {
    const siblingValues = (hasValue(next) ? onSelect?.(next) : onClear?.()) ?? {};
    setSelection(next);

    formikBag.setFieldTouched(name, true, false);
    SetFormikValues(formikBag, { [name]: next?.id ?? '', ...siblingValues });
  };

  const error = meta.touched && hasStringValue(meta.error) ? meta.error : undefined;

  return (
    <EntitySearchInputBase
      value={selection}
      onChange={handleChange}
      entityTypes={entityTypes}
      label={label}
      placeholder={placeholder}
      disabled={disabled}
      error={error}
      tip={tip}
      limitPerEntity={limitPerEntity}
      onBlur={(): void => {
        formikBag.setFieldTouched(name, true);
      }}
    />
  );
};
